import React, { useEffect, useMemo, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useKnownSwimResearch } from './KnownSwimResearchContext';
import { useKnownSwims } from './KnownSwimsContext';
import AreaResearchPanel, { editableAreaResearch, emptyAreaResearch, normalizeAreaResearch } from './AreaResearchPanel';

const formatCreatedAt = (createdAt) => {
  if (!createdAt?.toDate) return 'Unknown creation date';
  return createdAt.toDate().toLocaleString();
};

const applyCompletedResearch = (swims, researchResults) => swims.map((swim) => {
  const result = researchResults.get(swim.id);
  if (!result) return swim;

  return {
    ...swim,
    ...(result.appliedName ? { name: result.appliedName } : {}),
    hasResearch: true,
    areaResearch: normalizeAreaResearch(result.areaResearch)
  };
});

const KnownSwimsListPage = ({ onEdit }) => {
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searching, setSearching] = useState(false);
  const [isSearchResult, setIsSearchResult] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [researchBySwimId, setResearchBySwimId] = useState({});
  const [selectedSwimId, setSelectedSwimId] = useState(null);
  const [editingResearch, setEditingResearch] = useState(false);
  const [savingResearch, setSavingResearch] = useState(false);
  const { researchSwim: startResearch, isResearching, researchError, researchResults } = useKnownSwimResearch();
  const { knownSwims, loading, error: knownSwimsError, ensureKnownSwims } = useKnownSwims();

  useEffect(() => {
    ensureKnownSwims();
  }, [ensureKnownSwims]);

  const swims = useMemo(() => {
    const source = isSearchResult ? (searchResult ? [searchResult] : []) : knownSwims;
    const withResearch = source.map((swim) => ({
      ...swim,
      ...(researchBySwimId[swim.id] || {})
    }));
    return applyCompletedResearch(withResearch, researchResults)
      .sort((first, second) => (second.createdAt?.toMillis?.() || 0) - (first.createdAt?.toMillis?.() || 0));
  }, [isSearchResult, knownSwims, researchBySwimId, researchResults, searchResult]);

  const handleResearch = async (knownSwimId) => {
    try {
      const result = await startResearch(knownSwimId);
      setResearchBySwimId((current) => ({
        ...current,
        [knownSwimId]: {
          hasResearch: true,
          areaResearch: normalizeAreaResearch(result.areaResearch)
        }
      }));
    } catch (researchFailure) {
      console.error('Unable to research known swim:', researchFailure);
    }
  };

  const searchKnownSwims = async (queryText) => {
    const normalizedQuery = queryText.trim();
    if (!normalizedQuery) return;

    setSearching(true);
    setError(null);
    setSelectedSwimId(null);
    setEditingResearch(false);
    try {
      // The search contract accepts a general string; currently it resolves exact document IDs.
      const knownSwim = knownSwims.find((swim) => swim.id === normalizedQuery);
      if (!knownSwim) {
        setSearchResult(null);
        setIsSearchResult(true);
        setError(`No known swim found for "${normalizedQuery}".`);
        return;
      }

      setSearchResult({
        ...knownSwim
      });
      setIsSearchResult(true);
    } catch (searchError) {
      console.error('Unable to search known swims:', searchError);
      setError('Unable to search known swims.');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchText('');
    setIsSearchResult(false);
    setSelectedSwimId(null);
    setEditingResearch(false);
    setSearchResult(null);
  };

  const selectedSwim = swims.find((swim) => swim.id === selectedSwimId) || null;

  const selectSwim = (knownSwimId) => {
    setSelectedSwimId((current) => {
      const next = current === knownSwimId ? null : knownSwimId;
      return next;
    });
    setEditingResearch(false);
  };

  const updateSelectedResearch = (field, value) => {
    setResearchBySwimId((current) => ({
      ...current,
      [selectedSwimId]: {
        hasResearch: true,
        areaResearch: { ...(current[selectedSwimId]?.areaResearch || emptyAreaResearch), [field]: value }
      }
    }));
  };

  const saveSelectedResearch = async () => {
    if (!selectedSwim?.areaResearch) return;
    setSavingResearch(true);
    try {
      const savedResearch = {
        ...editableAreaResearch(selectedSwim.areaResearch),
        editedManually: true
      };
      await setDoc(doc(db, 'KnownSwims', selectedSwim.id, 'research', 'area'), {
        ...savedResearch,
        updatedAt: serverTimestamp()
      }, { merge: true });
      setResearchBySwimId((current) => ({
        ...current,
        [selectedSwim.id]: {
          hasResearch: true,
          areaResearch: { ...selectedSwim.areaResearch, ...savedResearch }
        }
      }));
      setEditingResearch(false);
    } catch (saveError) {
      console.error('Unable to save swim area research:', saveError);
      setError('Unable to save swim area research.');
    } finally {
      setSavingResearch(false);
    }
  };

  const cancelSelectedResearch = async () => {
    if (!selectedSwim) return;
    setEditingResearch(false);
    setResearchBySwimId((current) => Object.fromEntries(
      Object.entries(current).filter(([id]) => id !== selectedSwim.id)
    ));
  };

  if (loading) return <div className="known-swims-list-page">Loading known swims...</div>;

  return (
    <section className="known-swims-list-page">
      <h2>Known Swims List</h2>
      <form
        className="known-swims-list-search"
        onSubmit={(event) => {
          event.preventDefault();
          searchKnownSwims(searchText);
        }}
      >
        <input
          id="known-swims-search"
          aria-label="Search curated swims"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Curated swim ID"
        />
        <button type="submit" disabled={searching || !searchText.trim()}>
          {searching ? 'Searching...' : 'Search'}
        </button>
        {isSearchResult && <button type="button" className="secondary" onClick={clearSearch}>Clear</button>}
      </form>
      {(error || knownSwimsError) && <p className="error">{error || knownSwimsError}</p>}
      <div className="known-swims-list">
        {swims.map((swim) => (
          <React.Fragment key={swim.id}>
            <article className="known-swims-list-row">
              <div>
                <h3><button className="known-swims-list-name" onClick={() => selectSwim(swim.id)}>{swim.name || 'Untitled swim'}</button></h3>
                <p>{formatCreatedAt(swim.createdAt)}</p>
                {researchError(swim.id) && <p className="error">{researchError(swim.id)}</p>}
              </div>
              <div className="known-swims-list-actions">
                {swim.hasResearch ? (
                  <span className="known-swims-research-complete" aria-label="Area research available">✓ Research complete</span>
                ) : (
                  <button onClick={() => handleResearch(swim.id)} disabled={isResearching(swim.id)}>
                    {isResearching(swim.id) ? 'Researching...' : 'Research'}
                  </button>
                )}
                <button className="secondary" onClick={() => onEdit(swim)}>Edit</button>
              </div>
            </article>
            {selectedSwimId === swim.id && (
              <div className="known-swims-list-research">
                <AreaResearchPanel
                  research={selectedSwim?.areaResearch}
                  loading={false}
                  editing={editingResearch}
                  researching={isResearching(swim.id)}
                  saving={savingResearch}
                  canResearch
                  onResearch={() => handleResearch(swim.id)}
                  onEdit={() => {
                    setResearchBySwimId((current) => ({
                      ...current,
                      [swim.id]: {
                        hasResearch: true,
                        areaResearch: current[swim.id]?.areaResearch || { ...emptyAreaResearch }
                      }
                    }));
                    setEditingResearch(true);
                  }}
                  onCancel={cancelSelectedResearch}
                  onSave={saveSelectedResearch}
                  onChange={updateSelectedResearch}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      {swims.length === 0 && <p>No known swims yet.</p>}
    </section>
  );
};

export default KnownSwimsListPage;
